import { useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';

import useToast from '@hooks/useToast';
import { fetchShareHistory, shareResults } from '@store/actions';

import { SharingHistoryItem } from '../ShareResultsForm/ShareResults.types';
import ShareHistoryItem from './ShareHistoryItem';

/**
 * Parse ISO date string to Date object
 */
const parseDate = (dateString: string): Date => {
  return new Date(dateString);
};

/**
 * Generate a unique ID for an item (used when API doesn't provide one)
 */
const generateItemId = (item: SharingHistoryItem, index: number): string => {
  return item.id || `${item.email}-${index}`;
};

interface SharingHistoryProps {
  refreshTrigger?: number;
}

const SharingHistory = ({ refreshTrigger }: SharingHistoryProps) => {
  const { addToast } = useToast();
  const [sendingUpdateIds, setSendingUpdateIds] = useState<Set<string>>(new Set());
  const [sharingHistory, setSharingHistory] = useState<SharingHistoryItem[]>([]);
  const [recentlyUpdatedId, setRecentlyUpdatedId] = useState<string | null>(null);
  const [animationKey, setAnimationKey] = useState(0); // Force re-trigger animation for same item
  const [loading, setLoading] = useState(true);

  // Track request sequence to handle race conditions
  const requestSequenceRef = useRef<Map<string, number>>(new Map());
  const sequenceNumberRef = useRef(0);

  useEffect(() => {
    const loadHistory = async () => {
      try {
        setLoading(true);
        const history = await fetchShareHistory();
        // Convert API response to SharingHistoryItem if needed
        // Assuming API response matches SharingHistoryItem interface roughly or we cast it
        // based on the response type in actions/index.ts
        setSharingHistory(history as unknown as SharingHistoryItem[]);
      } catch (error) {
        console.error('Failed to load sharing history:', error);
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, [refreshTrigger]);

  // Generate IDs for items that don't have them (for tracking purposes)
  const sharingHistoryWithIds = useMemo(() => {
    return sharingHistory.map((item, index) => ({
      ...item,
      id: generateItemId(item, index),
    }));
  }, [sharingHistory]);

  const updatesAvailable = sharingHistoryWithIds.filter(item => !item.is_up_to_date);

  // Sort upToDate by date (most recent first)
  const upToDate = useMemo(() => {
    const upToDateItems = sharingHistoryWithIds.filter(item => item.is_up_to_date);
    return upToDateItems.sort((a, b) => {
      const dateA = parseDate(a.created_at);
      const dateB = parseDate(b.created_at);
      return dateB.getTime() - dateA.getTime(); // Most recent first
    });
  }, [sharingHistoryWithIds]);

  const handleSendUpdatedResults = async (item: SharingHistoryItem) => {
    // Use email as the identifier since id might not exist
    const itemId = item.id || item.email;

    // Track this request sequence to handle race conditions
    const currentSequence = ++sequenceNumberRef.current;
    requestSequenceRef.current.set(itemId, currentSequence);

    // Add item ID to sending set
    setSendingUpdateIds(prev => new Set(prev).add(itemId));

    try {
      // Use shareResults to resend/update
      const response = await shareResults({
        email: item.email,
        ...(item.name && { name: item.name }),
      });

      // Only process if this is still the most recent request for this item
      if (requestSequenceRef.current.get(itemId) === currentSequence) {
        // Trigger animation for the updated item (happens on every successful share)
        // Reset to null first, then set to itemId to ensure React detects the change
        setRecentlyUpdatedId(null);
        setAnimationKey(prev => prev + 1); // Increment to force re-trigger

        // Use setTimeout to ensure state update happens after reset
        setTimeout(() => {
          setRecentlyUpdatedId(itemId);
        }, 0);

        // Clear animation trigger after animation completes
        setTimeout(() => {
          setRecentlyUpdatedId(null);
        }, 2000);

        // Update the local state to reflect the item is up to date
        // If the API returned the full list (unlikely based on type definition but checking just in case)
        if (Array.isArray(response)) {
          setSharingHistory(response as unknown as SharingHistoryItem[]);
        } else {
          // Single item updated
          const now = new Date().toISOString();
          setSharingHistory(prevHistory => {
            const updatedHistory = prevHistory.map(historyItem => {
              if (historyItem.email === item.email) {
                return {
                  ...historyItem,
                  is_up_to_date: true,
                  created_at: response.created_at || now,
                };
              }
              return historyItem;
            });
            return updatedHistory;
          });
        }

        addToast({
          headerTitle: 'Success',
          content: `Updated results sent to ${item.email}`,
          variant: 'success',
          autohide: true,
          delay: 3000, // Auto-dismiss after 3 seconds
        });
      }
    } catch (error) {
      // Error toast is handled automatically by axios interceptor
      console.error('Error sending updated results:', error);
    } finally {
      // Remove item ID from sending set
      setSendingUpdateIds(prev => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  };

  const hasNoItems = sharingHistory.length === 0;

  return (
    <div className='d-flex flex-column gap-4'>
      <h5 className='text-primary mb-0'>Sharing History</h5>

      {loading && hasNoItems ? (
        <p className='d-flex align-items-center gap-2 text-primary'>
          <Spinner animation='border' size='sm' />
          Loading...
        </p>
      ) : hasNoItems ? (
        <div className='d-flex flex-column gap-2 '>
          <p className='mb-0 text-secondary fs-sm fw-semibold'>No sharing history yet.</p>
          <p className='mb-0 text-secondary fs-xs'>When you share your results, recipients will appear here.</p>
        </div>
      ) : (
        <>
          {/* Updates Available Section */}
          {updatesAvailable.length > 0 && (
            <div>
              <p className='fw-bold text-secondary fs-xs mb-2'>Updates Available</p>
              <div className='d-flex flex-column gap-3'>
                {updatesAvailable.map(item => {
                  const itemId = item.id || item.email;
                  return (
                    <ShareHistoryItem
                      key={itemId}
                      item={item}
                      isUpToDate={false}
                      sendingUpdateIds={sendingUpdateIds}
                      onSendUpdatedResults={handleSendUpdatedResults}
                      shouldAnimate={recentlyUpdatedId === itemId}
                      animationKey={animationKey}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Up to Date Section */}
          {upToDate.length > 0 && (
            <div>
              <p className='fw-bold text-secondary fs-xs mb-2'>Up to Date</p>
              <div className='d-flex flex-column gap-3'>
                {upToDate.map(item => {
                  const itemId = item.id || item.email;
                  return (
                    <ShareHistoryItem
                      key={itemId}
                      item={item}
                      isUpToDate={true}
                      sendingUpdateIds={sendingUpdateIds}
                      onSendUpdatedResults={handleSendUpdatedResults}
                      shouldAnimate={recentlyUpdatedId === itemId}
                      animationKey={animationKey}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SharingHistory;
